import Vue from "vue";
import VueRouter from "vue-router";
import Register from "@/views/Register";
import Login from "../views/Login.vue";
import options from "../../options";
import store from "../store";
import {
  isKonekoAccessControlUser,
  profileUsernameFromJwt,
} from "@/config/konekoAConfig";
import {
  canonicalizeKonekoUsername,
  getKonekoCapabilities,
  KONEKO_CAP,
} from "@/config/konekoVisibility";
import {syncDashboardViewAsFromStoreGetter} from "@/utils/dashboardViewAsSync";
import ForgotPassword from "@/views/ForgotPassword";
import ResetPassword from "@/views/ResetPassword";
import KonekoAView from "@/views/KonekoAView.vue";
import KonekoAAccessAdminView from "@/views/KonekoAAccessAdminView.vue";
import VueJwtDecode from "vue-jwt-decode";

Vue.use(VueRouter);

/** vuex-persistedstate can lag one tick — read token before rejecting /koneko-a. */
function ensureAuthFromStoredToken() {
  if (store.getters.isAuthenticated) return true;
  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    VueJwtDecode.decode(token);
    store.commit("AUTH_SUCCESS", token);
    return true;
  } catch {
    return false;
  }
}

const routes = [
  {
    path: "/",
    name: "Home",
    component: () => import("@/views/DashboardView.vue"),
    meta: {
      hideHeader: true,
      requiresAuth: true,
    },
  },
  {
    path: "/dashboard",
    name: "DashboardPage",
    component: () => import("@/views/DashboardView.vue"),
    meta: {
      hideHeader: true,
      requiresAuth: true,
    },
  },
  {
    path: "/dashboard-legacy",
    redirect: {name: "DashboardPage"},
  },
  {
    path: "/profile",
    name: "Profile",
    component: () => import("../components/User/Profile.vue"),
    meta: {
      hideHeader: true,
      requiresAuth: true,
    },
  },
  {
    path: "/timesheet/viewtimesheet",
    name: "Timesheet",
    component: () => import("@/views/TimeSheetView.vue"),
    meta: {
      hideHeader: true,
      requiresAuth: true,
      konekoAccessControlOnly: true,
    },
  },
  {
    path: "/projects",
    name: "Projects",
    component: () => import("@/views/ProjectView.vue"),
    meta: {
      hideHeader: true,
      requiresAuth: true,
    },
  },
  {
    path: "/members",
    name: "Member",
    component: () => import("@/views/MemberView.vue"),
    meta: {
      hideHeader: true,
      requiresAuth: true,
    },
  },
  {
    path: "/members/:userId",
    name: "MemberInfo",
    component: () => import("../components/User/Profile.vue"),
    props: true,
    meta: {
      hideHeader: true,
      requiresAuth: true,
    },
  },
  {
    path: "/notification/alert",
    name: "Alerts",
    component: () => import("../components/Notification/Alerts.vue"),
    meta: {
      hideHeader: true,
      requiresAuth: true,
    },
  },
  {
    path: "/timer",
    name: "Timer",
    component: () => import("@/views/TimerView.vue"),
    meta: {
      hideHeader: true,
      requiresAuth: true,
    },
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/register",
    name: "Register",
    component: Register,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/report",
    name: "Report",
    component: Report,
    meta: {
      requiresAuth: false,
      hideHeader: true,
      specialRoute: true,
    },
  },
  {
    path: "/forgot_password",
    name: "ForgotPassword",
    component: ForgotPassword,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/reset_password",
    name: "ResetPassword",
    component: ResetPassword,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/inner_card",
    name: "InnerCard",
    component: () => import("../components/Projects/ProjectInnerCard.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/team_detail",
    name: "TeamDetail",
    component: () => import("../components/Team/ViewTeamDetail.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/koneko-a",
    name: "KonekoA",
    // Eager load — lazy chunk failures left users stuck on Dashboard.
    component: KonekoAView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/koneko-a/access",
    name: "KonekoAAccess",
    component: KonekoAAccessAdminView,
    meta: {
      requiresAuth: true,
      konekoPlanningAdminOnly: true,
    },
  },
  {
    path: "/payroll",
    name: "Payroll",
    component: () => import("@/views/PayrollView.vue"),
    meta: {
      requiresAuth: true,
      hideHeader: true,
      konekoPayrollOnly: true,
    },
  },
  {
    path: "/koneko-b",
    name: "KonekoB",
    component: () => import("@/views/KonekoBView.vue"),
    meta: {
      requiresAuth: true,
      hideHeader: true,
    },
  },
];

const routerParams = {
  ...options.routeAdditionalParams,
  routes,
};

const router = new VueRouter(routerParams);

import Router from "vue-router";
import Report from "@/views/Report";
const routerPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return routerPush.call(this, location).catch((error) => error);
};

function isKonekoARoute(to) {
  return to.name === "KonekoA" || to.path === "/koneko-a";
}

router.beforeEach((to, from, next) => {
  const pageRequiresAuth = to.matched.some(
    (record) => record.meta.requiresAuth
  );
  if (pageRequiresAuth && !store.getters.isAuthenticated) {
    ensureAuthFromStoredToken();
  }
  const isAuthenticated = store.getters.isAuthenticated;

  syncDashboardViewAsFromStoreGetter(
    store.getters.userProfileData,
    isAuthenticated
  );

  const specialRoute = to.matched.some((record) => record.meta.specialRoute);

  if (pageRequiresAuth && !isAuthenticated) {
    // This page requires Auth but you are not Logged in
    next({name: "Login"});
    return;
  } else if (!pageRequiresAuth && isAuthenticated && !specialRoute) {
    // This page requires No Auth, but you ARE Logged in
    // e.g. you are logged in and then try to go to /login
    next({name: "Home"});
    return;
  }

  const accessControlOnly = to.matched.some(
    (record) => record.meta.konekoAccessControlOnly
  );
  if (
    accessControlOnly &&
    !isKonekoAccessControlUser(store.getters.userProfileData)
  ) {
    next({name: "Home"});
    return;
  }

  const konekoAdminOnly = to.matched.some(
    (record) => record.meta.konekoPlanningAdminOnly
  );
  if (
    konekoAdminOnly &&
    !isKonekoAccessControlUser(store.getters.userProfileData)
  ) {
    next({name: "KonekoA"});
    return;
  }

  const payrollOnly = to.matched.some(
    (record) => record.meta.konekoPayrollOnly
  );
  if (payrollOnly) {
    const profile = store.getters.userProfileData;
    const who =
      profileUsernameFromJwt(profile) ||
      canonicalizeKonekoUsername(profile && profile.username);
    const caps = getKonekoCapabilities(who);
    if (!caps[KONEKO_CAP.navPayroll]) {
      next({name: "Home"});
      return;
    }
  }

  next();
});

router.onError((err) => {
  const msg = err && err.message ? String(err.message) : "";
  if (
    /Loading chunk [\w-]+ failed|Loading CSS chunk [\w-]+ failed|ChunkLoadError/i.test(
      msg
    )
  ) {
    console.warn("Route chunk load failed — reloading for fresh assets.", err);
    const hash = window.location.hash || "";
    window.location.replace(
      `${window.location.pathname}${window.location.search}${hash}`
    );
    window.location.reload();
    return;
  }
  if (/Maximum call stack size exceeded/i.test(msg)) {
    console.error("Koneko A render stack overflow.", err);
    if (isKonekoARoute(router.currentRoute)) {
      window.location.hash = "#/koneko-a";
      window.location.reload();
    }
  }
});

export default router;
